// client/src/components/stats/cbetTextureBenchmarks.ts
//
// Benchmarks de CBet Flop por textura × posición (HU only).
// Source: theoretical — consensus de solver outputs (PIO, Monker, Wizard)
// para situaciones SRP HU en posiciones generales.
//
// Los rangos son aproximados. Los drivers reales de cbet óptimo exacto son:
//   - SPR exacto del flop
//   - Textura específica dentro de la categoría (K72 dry ≠ 987 dry)
//   - Rango preflop del hero (range advantage)
//   - Tendencias del villain pool
//
// Pero a nivel de categoría × posición, los solvers coinciden bastante bien
// en estos rangos. Sirve como baseline para marcar leaks direccionales.

import { CbetTexturePosKey } from '../../utils/pokerStats/types';
import { Benchmark } from './benchmarks';

export const CBET_TEXTURE_BENCHMARKS: Record<CbetTexturePosKey, Benchmark> = {
  // ─── Dry boards — máximo cbet (range advantage domina) ───────────────────
  // Ej: K72 rainbow, A92 rainbow, Q83 rainbow. El PFR tiene enorme advantage
  // en manos Ax/Kx/overcards y el villain rara vez conecta fuerte.
  'dry-ip':  { min: 70, max: 85, source: 'theoretical' },
  'dry-oop': { min: 55, max: 70, source: 'theoretical' },

  // ─── Semi-wet boards — cbet alto pero más selectivo ──────────────────────
  // Ej: J94 con flush draw, KT5 con flush draw. Hay algo de equity para el
  // villain pero el range advantage del PFR sigue siendo sólido.
  'semi-wet-ip':  { min: 55, max: 70, source: 'theoretical' },
  'semi-wet-oop': { min: 40, max: 55, source: 'theoretical' },

  // ─── Wet boards — cbet selectivo, más checks ─────────────────────────────
  // Ej: 987 two-tone, T98 one-suit. Muchos draws + muchas 2-pair posibles.
  // El villain conecta seguido, range advantage del PFR es más débil.
  'wet-ip':  { min: 40, max: 55, source: 'theoretical' },
  'wet-oop': { min: 25, max: 40, source: 'theoretical' },

  // ─── Paired boards — casi siempre cbet (range advantage absoluto) ────────
  // Ej: KK7, 884, TT2. El PFR tiene mucho más advantage porque las trips
  // están balanceadas hacia su rango. Casi siempre se batea.
  'paired-ip':  { min: 70, max: 85, source: 'theoretical' },
  'paired-oop': { min: 55, max: 70, source: 'theoretical' },

  // ─── Monotone boards — cbet SELECTIVO (hay flush draws / flushes hechos) ─
  // Ej: K♠7♠2♠. El villain tiene mucha equity con one-card flush draws
  // y todas las flushes hechas se concentran contra hero. Bajar frecuencia.
  'monotone-ip':  { min: 30, max: 50, source: 'theoretical' },
  'monotone-oop': { min: 20, max: 40, source: 'theoretical' },
};

export function getCbetTextureBenchmark(key: CbetTexturePosKey): Benchmark | null {
  return CBET_TEXTURE_BENCHMARKS[key] || null;
}
